import { STOREFRONT_CATEGORY_REM } from "@/domain/storefront-appearance";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { Category } from "@/types";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui";

function horizontalScroller(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowX } = getComputedStyle(node);
    if (overflowX === "auto" || overflowX === "scroll") return node;
  }
  return null;
}

function centerInRow(el: HTMLElement): void {
  const row = horizontalScroller(el);
  if (!row) return;
  const offset = el.getBoundingClientRect().left - row.getBoundingClientRect().left;
  const left = row.scrollLeft + offset - (row.clientWidth - el.offsetWidth) / 2;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  row.scrollTo({ left, behavior: reduce ? "auto" : "smooth" });
}

export default function CategoriesNavigation({
  category,
  categoryInViewId,
  onCategoryClick,
}: {
  category: Category;
  categoryInViewId: string;
  onCategoryClick: (categoryId: string) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { categoryNavSize } = useStorefrontAppearance();

  useEffect(() => {
    if (category.id === categoryInViewId && buttonRef.current) centerInRow(buttonRef.current);
  }, [categoryInViewId, category.id]);

  function handleClick() {
    onCategoryClick(category.id);
  }

  if (!category.itemOfferId) return null;

  const isActive = categoryInViewId === category.id;

  return (
    <Button
      ref={buttonRef}
      variant="tab"
      tone="primary"
      size="tab"
      weight="semibold"
      selected={isActive}
      onClick={handleClick}
      data-testid="category-nav-item"
      style={{ fontSize: `${STOREFRONT_CATEGORY_REM[categoryNavSize]}rem` }}
      className="whitespace-nowrap"
    >
      {category.name.toUpperCase()}
    </Button>
  );
}
